import { useState } from "react";

import { ActionIcon, CopyButton, rem, Text, Tooltip } from "@mantine/core";
import {
  IconBook,
  IconCheck,
  IconCopy,
  IconPhoneCall,
  IconSearch,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { TextInput } from "components";
import { AppConstants, RouteConstants } from "constant";
import { CallScriptsTrackingService } from "services";
import { UserResponse } from "types";
import { StorageUtils } from "utils";

import { CallScriptsModal, ScriptEndType, ScriptModuleId } from "./call-scripts";

import classes from "./CallWidgetSection.module.css";

type CallAction = "success" | "failure" | "resolved" | null;
type SuccessResult = "requestPreparation" | "notInterested" | "scheduledFollowUp" | null;
type FailureResult = "notReachedCombox" | "combox" | "notReached" | "busy" | null;

const mockClient = {
  id: 2481,
  fullName: "Aleksandra Aleksić",
  countryCode: "+41",
  phoneNumber: "72 788 8166",
  language: "SR",
  weekdayAvailability: {
    from: "15:00",
    to: "19:00",
  },
  saturdayAvailability: {
    from: "10:00",
    to: "20:00",
  },
  hasCreditCard: true,
  hasLeasing: false,
  planningCredit: true,
  hasCredit: false,
  app: true,
};

interface ToggleStatusItemProps {
  active: boolean;
  app?: boolean;
  label: string;
}

const ToggleStatusItem = ({ active, app, label }: ToggleStatusItemProps) => {
  return (
    <div className={classes.statusItem}>
      <span className={classes.statusLabel}>{label}</span>

      {app ? (
        <span className={classes.appIndicator}>
          {active ? <IconCheck size={16} stroke={2.4} /> : null}
        </span>
      ) : (
        <span className={`${classes.switch} ${active ? classes.switchActive : classes.switchInactive}`}>
          <span className={classes.switchThumb} />
        </span>
      )}
    </div>
  );
};

interface TimeAvailabilityProps {
  from: string;
  label: string;
  to: string;
  t: (key: string) => string;
}

const TimeAvailability = ({ from, label, to, t }: TimeAvailabilityProps) => {
  return (
    <div className={classes.infoBlock}>
      <Text className={classes.infoTitle}>{label}</Text>

      <div className={classes.timeRow}>
        <Text className={classes.timeLabel}>{t("dashboard.call.fields.from")}</Text>
        <div className={classes.timeValue}>{from}</div>
        <Text className={classes.timeLabel}>{t("dashboard.call.fields.to")}</Text>
        <div className={classes.timeValue}>{to}</div>
      </div>
    </div>
  );
};

interface OutcomeOptionProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

const OutcomeOption = ({ active, label, onClick }: OutcomeOptionProps) => {
  return (
    <button className={classes.outcomeOption} onClick={onClick} type="button">
      <span className={`${classes.outcomeRadio} ${active ? classes.outcomeRadioActive : ""}`}>
        {active ? <span className={classes.outcomeRadioDot} /> : null}
      </span>
      <span>{label}</span>
    </button>
  );
};

interface ActionButtonProps {
  active: boolean;
  ariaLabel: string;
  children?: React.ReactNode;
  onClick: () => void;
  tone: "success" | "failure" | "resolved";
}

const ActionButton = ({ active, ariaLabel, children, onClick, tone }: ActionButtonProps) => {
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={active}
      className={`${classes.actionButton} ${classes[`actionButton${tone.charAt(0).toUpperCase()}${tone.slice(1)}`]} ${
        active ? classes.actionButtonActive : ""
      }`}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
};

const CallWidgetSection = () => {
  const { t } = useTranslation();
  const [selectedAction, setSelectedAction] = useState<CallAction>(null);
  const [selectedSuccessResult, setSelectedSuccessResult] = useState<SuccessResult>(null);
  const [selectedFailureResult, setSelectedFailureResult] = useState<FailureResult>(null);
  const [scriptsOpen, setScriptsOpen] = useState(false);
  const [usedScript, setUsedScript] = useState<{
    moduleId: ScriptModuleId;
    endType: ScriptEndType;
  } | null>(null);

  const user = StorageUtils.getObject(AppConstants.STORAGE_USER) as UserResponse | undefined;
  const agentName = user?.fullName ?? "";
  const clientName = mockClient.fullName;

  const profilePath = `${RouteConstants.ROUTE_CLIENTS}/${mockClient.id}`;
  const phoneNumber = `${mockClient.countryCode} ${mockClient.phoneNumber}`;
  const languageLabel = t(`user.language.options.${mockClient.language}`);
  const hasActionSelection = selectedAction !== null;
  const successOptions = [
    {
      value: "requestPreparation" as const,
      label: t("dashboard.call.outcomes.success.options.requestPreparation"),
    },
    {
      value: "notInterested" as const,
      label: t("dashboard.call.outcomes.success.options.notInterested"),
    },
    {
      value: "scheduledFollowUp" as const,
      label: t("dashboard.call.outcomes.success.options.scheduledFollowUp"),
    },
  ];
  const failureOptions = [
    {
      value: "notReachedCombox" as const,
      label: t("dashboard.call.outcomes.failure.options.notReachedCombox"),
    },
    {
      value: "combox" as const,
      label: t("dashboard.call.outcomes.failure.options.combox"),
    },
    {
      value: "notReached" as const,
      label: t("dashboard.call.outcomes.failure.options.notReached"),
    },
    {
      value: "busy" as const,
      label: t("dashboard.call.outcomes.failure.options.busy"),
    },
  ];

  const handleActionChange = (action: Exclude<CallAction, null>) => {
    setSelectedAction(action);

    if (action !== "success") {
      setSelectedSuccessResult(null);
    }

    if (action !== "failure") {
      setSelectedFailureResult(null);
    }
  };

  const handleSave = () => {
    // Record the call outcome, linked to the script the agent used (if any).
    CallScriptsTrackingService.trackCallScriptUsage({
      moduleId: usedScript?.moduleId ?? null,
      endType: usedScript?.endType ?? null,
      clientId: mockClient.id,
      clientName,
      agentId: user?.id ?? null,
      agentName,
      callAction: selectedAction,
      successResult: selectedSuccessResult,
      failureResult: selectedFailureResult,
      occurredAt: new Date().toISOString(),
    });
  };

  return (
    <div className={classes.root}>
      <TextInput
        className={classes.search}
        leftSection={<IconSearch size={20} stroke={1.9} />}
        leftSectionPointerEvents="none"
        placeholder={t("dashboard.call.searchPlaceholder")}
        radius="xl"
        size="md"
        styles={{
          input: {
            height: rem(42),
            borderColor: "var(--mantine-color-gray-4)",
            color: "var(--mantine-color-gray-7)",
            fontSize: rem(15),
            fontWeight: 600,
            paddingLeft: rem(46),
          },
          section: {
            color: "var(--mantine-color-gray-5)",
            width: rem(42),
          },
        }}
      />

      <div className={classes.content}>
        <Text className={classes.sourceTag}>{t("dashboard.call.todo.badge")}</Text>

        <div className={classes.header}>
          <div className={classes.headerMain}>
            <div className={classes.clientHeadingRow}>
              <Text className={classes.clientHeading}>{`${mockClient.fullName}  ${phoneNumber}`}</Text>

              <CopyButton value={phoneNumber} timeout={1200}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? t("dashboard.call.actions.copied") : t("dashboard.call.actions.copy")}
                    withArrow
                  >
                    <ActionIcon
                      aria-label={t("dashboard.call.actions.copy")}
                      className={classes.copyButton}
                      onClick={copy}
                      radius="xl"
                      variant="subtle"
                    >
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </div>

            <Text className={classes.sourceTitle}>{t("source.title")}</Text>
          </div>

          <RouterLink
            aria-label={t("dashboard.call.actions.openClient")}
            className={classes.profileLink}
            to={profilePath}
          >
            <IconUser size={20} stroke={1.9} />
          </RouterLink>
        </div>

        <div className={classes.detailsCard}>
          <div className={classes.detailsGrid}>
            <TimeAvailability
              from={mockClient.weekdayAvailability.from}
              label={t("dashboard.call.fields.weekdayAvailability")}
              t={t}
              to={mockClient.weekdayAvailability.to}
            />

            <TimeAvailability
              from={mockClient.saturdayAvailability.from}
              label={t("dashboard.call.fields.saturdayAvailability")}
              t={t}
              to={mockClient.saturdayAvailability.to}
            />

            <div className={classes.infoBlock}>
              <Text className={classes.infoTitle}>{t("dashboard.call.fields.language")}</Text>
              <Text className={classes.languageValue}>{languageLabel}</Text>
            </div>
          </div>

          <div className={classes.statusBar}>
            <ToggleStatusItem active={mockClient.hasCreditCard} label={t("dashboard.call.status.hasCreditCard")} />
            <ToggleStatusItem active={mockClient.hasLeasing} label={t("dashboard.call.status.hasLeasing")} />
            <ToggleStatusItem active={mockClient.planningCredit} label={t("dashboard.call.status.planningCredit")} />
            <ToggleStatusItem active={mockClient.hasCredit} label={t("dashboard.call.status.hasCredit")} />
            <ToggleStatusItem active={mockClient.app} app label={t("dashboard.call.status.app")} />
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.todoBlock}>
          <div className={`${classes.todoCheckbox} ${hasActionSelection ? classes.todoCheckboxChecked : ""}`}>
            {hasActionSelection ? <IconCheck size={17} stroke={2.5} /> : null}
          </div>

          <div className={classes.todoContent}>
            <Text className={classes.todoTitle}>{t("dashboard.call.todo.title")}</Text>
            <Text className={classes.todoDescription}>{t("dashboard.call.todo.description")}</Text>
          </div>
        </div>

        <button className={classes.scriptsButton} onClick={() => setScriptsOpen(true)} type="button">
          <IconBook size={20} stroke={1.9} />
          <span>{t("callScripts.openButton")}</span>
        </button>

        <div className={classes.actionsRow}>
          <ActionButton
            active={selectedAction === "success"}
            ariaLabel={t("dashboard.call.actions.success")}
            onClick={() => handleActionChange("success")}
            tone="success"
          >
            <IconPhoneCall size={22} stroke={2.1} />
          </ActionButton>

          <ActionButton
            active={selectedAction === "failure"}
            ariaLabel={t("dashboard.call.actions.failure")}
            onClick={() => handleActionChange("failure")}
            tone="failure"
          >
            <IconPhoneCall size={22} stroke={2.1} />
          </ActionButton>

          <ActionButton
            active={selectedAction === "resolved"}
            ariaLabel={t("dashboard.call.actions.resolvedWithoutCall")}
            onClick={() => handleActionChange("resolved")}
            tone="resolved"
          >
            {t("dashboard.call.actions.resolvedWithoutCall")}
          </ActionButton>
        </div>

        {selectedAction === "success" ? (
          <div className={classes.outcomeSection}>
            <Text className={`${classes.outcomeTitle} ${classes.outcomeTitleSuccess}`}>
              {t("dashboard.call.outcomes.success.title")}
            </Text>

            <div className={classes.outcomeOptions}>
              {successOptions.map((option) => (
                <OutcomeOption
                  active={selectedSuccessResult === option.value}
                  key={option.value}
                  label={option.label}
                  onClick={() => setSelectedSuccessResult(option.value)}
                />
              ))}
            </div>
          </div>
        ) : null}

        {selectedAction === "failure" ? (
          <div className={classes.outcomeSection}>
            <Text className={`${classes.outcomeTitle} ${classes.outcomeTitleFailure}`}>
              {t("dashboard.call.outcomes.failure.title")}
            </Text>

            <div className={classes.outcomeOptions}>
              {failureOptions.map((option) => (
                <OutcomeOption
                  active={selectedFailureResult === option.value}
                  key={option.value}
                  label={option.label}
                  onClick={() => setSelectedFailureResult(option.value)}
                />
              ))}
            </div>
          </div>
        ) : null}

        {selectedAction !== null && selectedAction !== "resolved" ? (
          <button className={classes.blacklistButton} type="button">
            <span className={classes.blacklistIcon}>
              <IconX size={15} stroke={2.6} />
            </span>
            <span>{t("dashboard.call.actions.blacklistClient")}</span>
          </button>
        ) : null}

        <button
          className={`${classes.saveButton} ${hasActionSelection ? classes.saveButtonEnabled : classes.saveButtonDisabled}`}
          disabled={!hasActionSelection}
          onClick={handleSave}
          type="button"
        >
          {t("dashboard.call.actions.save")}
        </button>
      </div>

      <CallScriptsModal
        agentName={agentName}
        clientName={clientName}
        onClose={() => setScriptsOpen(false)}
        onScriptCompleted={(moduleId, endType) => setUsedScript({ moduleId, endType })}
        opened={scriptsOpen}
      />
    </div>
  );
};

export default CallWidgetSection;
